/**
 * Vendored globe assets — never hotlink CDN at runtime.
 *
 * RESOLUTION LIMIT: globe.gl wraps a single equirectangular texture on a sphere
 * with no LOD / tile streaming. 8K (or ~5K Blue Marble) is the practical ceiling —
 * sharp at country/region framing, never street level. Street-level would need
 * the globe.gl tile-engine approach or Cesium (separate architecture).
 *
 * Lazy-load path: paint with low-res first, swap to hi-res when ready.
 */
export const GLOBE_ASSETS = {
  /** Low-res day preview (~2K) for first paint. */
  earthDay: "/assets/globe/earth-day.jpg",
  /** Hi-res day (NASA Blue Marble topo/bathy ~5400×2700 JPEG). */
  earthDayHi: "/assets/globe/earth-day-8k.jpg",
  /** Low-res night preview. */
  earthNight: "/assets/globe/earth-night.jpg",
  /** Hi-res night lights (~8K JPEG). */
  earthNightHi: "/assets/globe/earth-night-8k.jpg",
  nightSky: "/assets/globe/night-sky.png",
  clouds: "/assets/globe/clouds.png",
  /** Natural Earth 110m — low-res simplified polygons (~177 features). */
  countries: "/assets/globe/ne_110m_admin_0_countries.geojson",
} as const;

export const CLOUDS_ALT = 0.008;
/** Deg per frame at ~60fps from the official clouds example. */
export const CLOUDS_ROTATION_SPEED = -0.006;
export const CLOUDS_OPACITY = 0.45;

export const BORDER_STROKE = "rgba(200, 210, 220, 0.35)";
/** Path altitude — thin line above the surface, no polygon mesh. */
export const BORDER_ALTITUDE = 0.004;
export const BORDER_STROKE_WIDTH = 0.4;
/** Target country outline during shot preview/record. */
export const HIGHLIGHT_BORDER_STROKE = "rgba(30, 215, 96, 0.95)";
export const HIGHLIGHT_BORDER_STROKE_WIDTH = 1.35;
export const HIGHLIGHT_BORDER_ALTITUDE = 0.006;

/** Hide country labels beyond this camera altitude. */
export const LABEL_MAX_ALTITUDE = 2.15;
export const LABEL_SIZE = 0.35;
export const LABEL_SIZE_ARTIST = 0.42;
export const LABEL_COLOR = "rgba(210, 220, 230, 0.45)";
export const LABEL_COLOR_ARTIST = "rgba(230, 240, 250, 0.85)";
export const LABEL_ALTITUDE = 0.012;
/** Larger, always-on label for the shot target (preview + record). */
export const SHOT_LABEL_SIZE = 1.05;
export const SHOT_LABEL_COLOR = "rgba(255, 255, 255, 0.95)";
export const SHOT_LABEL_ALTITUDE = 0.02;

/** Dark halo under pins so green stays readable on the daylight texture. */
export const PIN_HALO_COLOR = "rgba(0, 0, 0, 0.4)";

/**
 * Closest camera altitude (globe radii above surface). Tight enough that a
 * mid-size country can fill the view; still stops short of mushy 8K texture.
 */
export const MIN_CAMERA_ALTITUDE = 0.85;
